import { Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/lib/useIsMobile";

/**
 * Banner shown to staff users whose account has `can_edit=false` OR to any
 * staff user viewing on a mobile viewport (mobile is view-only by design).
 * Makes the read-only state visible (so they don't think the app is broken
 * when Save buttons return 403s) and tells them who to ask if they need
 * edit access.
 *
 * Renders nothing for admins, for desktop editors, or for clients.
 */
export default function ViewOnlyBanner() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  if (!user) return null;
  // Admins always edit; clients have their own RBAC; only show for staff.
  if (user.role !== "staff") return null;
  // Desktop editors don't need the banner. Mobile staff always do.
  if (user.can_edit && !isMobile) return null;
  const mobileOnly = user.can_edit && isMobile;
  return (
    <div
      className="rounded-md border border-[#0088CC]/30 bg-[#E6F2FA]/60 px-4 py-3 mb-4 flex items-start gap-3"
      data-testid="view-only-banner"
    >
      <Eye className="h-4 w-4 text-[#0088CC] mt-0.5 shrink-0" />
      <div className="text-xs text-[#33475C] leading-relaxed">
        <span className="font-display font-semibold text-[#011836]">View-only access.</span>{" "}
        {mobileOnly ? (
          <>Editing is disabled on mobile — switch to a desktop browser to make changes.</>
        ) : (
          <>You can browse this page but can&apos;t create, edit or delete records. Ask an admin to enable editing on your account if you need it.</>
        )}
      </div>
    </div>
  );
}

